import { Typography } from 'antd';
import { useEffect, useRef, useState } from 'react';
import LoanFilters from '~/components/LoanFilters/LoanFilters';
import LoanUserTable from '~/components/LoanUserTable/LoanUserTable';
import ReferrerActionBar from '~/components/ReferrerActionBar/ReferrerActionBar';
import { DASHBOARD_DIV_ID, loanConfig } from '~/constants';
import { startNewApplication } from '~/services/SdkService';
import { getPartnersAndReferrers } from '~/services/authService';
import { fetchLoans } from '~/services/loanService';
import { useAuthStore } from '~/store/auth';
import { useLoansStore } from '~/store/loans';
import type { Partner } from '~/types/auth';
import type { SdkError } from '~/types/interaction';
import type { LoanFiltersType } from '~/types/loans';
import { defaultFilters, filtersKey } from '~/utils/constants';
import { initiateCSVDownload } from '~/utils/downloadCSV';
import notify from '~/utils/useNotification';
import { isDateRangeWithinLimit } from '~/utils/utilities';

const Dashboard = () => {
  const { partners } = useAuthStore();
  const { loans } = useLoansStore();
  const [scReferrers, setScReferrers] = useState<string[]>([]);
  const [selectedReferrer, setSelectedReferrer] = useState<string>('');
  const [toggleDashboard, setToggleDashboard] = useState<boolean>(false);
  const downloadCleanupRef = useRef<(() => void) | null>(null);
  const [filters, setFilters] = useState(() => {
    const storedFilters = JSON.parse(
      sessionStorage.getItem(filtersKey) || '{}',
    );
    return storedFilters
      ? { ...defaultFilters, ...storedFilters }
      : defaultFilters;
  });
  const appliedFiltersRef = useRef<LoanFiltersType>(filters);

  // Abort any ongoing download before fetching loans
  const abortDownloadAndFetchLoans = async (
    filtersToUse: LoanFiltersType,
  ): Promise<boolean> => {
    if (downloadCleanupRef.current) {
      downloadCleanupRef.current();
      downloadCleanupRef.current = null;
    }
    // Store the filters that are actually being applied
    appliedFiltersRef.current = filtersToUse;
    return fetchLoans(filtersToUse);
  };

  const resetFilters = () => {
    setFilters(defaultFilters);
    sessionStorage.setItem(filtersKey, JSON.stringify(defaultFilters));
    abortDownloadAndFetchLoans(defaultFilters);
  };

  const fetchPartnersAndReferrersAndLoans = async () => {
    const updatedFilters = { ...filters, page: 1 };
    const partnersList = await getPartnersAndReferrers();
    partnersList.sort((a: Partner, b: Partner) =>
      a.partner.localeCompare(b.partner),
    );
    if (!updatedFilters.partner && partnersList.length > 0) {
      updatedFilters.partner = partnersList[0].partner;
    }
    if (
      !isDateRangeWithinLimit(
        updatedFilters.fromDate,
        updatedFilters.toDate,
        loanConfig.maxLoanFilterDateRangeInMonths,
      )
    ) {
      notify({
        type: 'warning',
        title: 'Date range exceeded',
        description: `Date range should not exceed ${loanConfig.maxLoanFilterDateRangeInMonths} months`,
      });
      return;
    }
    const success = await abortDownloadAndFetchLoans(updatedFilters);
    if (success) {
      setFilters(updatedFilters);
      sessionStorage.setItem(filtersKey, JSON.stringify(updatedFilters));
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    fetchPartnersAndReferrersAndLoans();
  }, []);

  useEffect(() => {
    //checks if the partner is mfd partner
    const scPartner = partners.find((partner) => partner.isMfdPartner) || {
      referrers: [],
    };
    scPartner.referrers.sort((a, b) => a.localeCompare(b));
    if (scPartner) {
      setScReferrers(scPartner.referrers);
      setSelectedReferrer(scPartner.referrers[0]);
    }
  }, [partners]);

  const handleReferrerSelect = (value: string) => {
    setSelectedReferrer(value);
  };

  const scReferrersOption = scReferrers.map((referrer) => ({
    label: referrer,
    value: referrer,
  }));

  //check if the selected partner is mfd partner
  const isCurrentPartnerMfd = () => {
    const partner = JSON.parse(
      sessionStorage.getItem(filtersKey) || '{}',
    ).partner;
    return partners.find((p) => p.partner === partner)?.isMfdPartner || false;
  };

  //start the guest flow application journey for the mfd partner
  const startApplication = async () => {
    const [ok, result] = await startNewApplication(selectedReferrer);
    if (!ok) {
      notify({
        type: 'error',
        title: 'Gateway SDK returned an error',
        description: (result as SdkError).message,
      });
    }
  };

  // Download CSV file
  const handleDownloadCSV = async (): Promise<void> => {
    // Pre-check: abort if no loans
    if (loans.length === 0) {
      notify({
        type: 'warning',
        title: 'No data to download',
        description: 'There are no loans to export.',
      });
      return;
    }

    // Clean up any existing polling
    if (downloadCleanupRef.current) {
      downloadCleanupRef.current();
      downloadCleanupRef.current = null;
    }

    // Use the filters that were actually applied to fetch the current loans
    const { page, limit, ...downloadFilters } = appliedFiltersRef.current;

    try {
      // Initiate download with polling
      await initiateCSVDownload(downloadFilters, (cleanup) => {
        downloadCleanupRef.current = cleanup;
      });
      // Promise resolves on success - no notification needed per spec
    } catch (error) {
      // Error callback
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'An error occurred during download.';
      notify({
        type: 'error',
        title: 'Download failed',
        description: errorMessage,
      });
      throw error; // Re-throw so button can handle loading state
    }
  };

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (downloadCleanupRef.current) {
        downloadCleanupRef.current();
      }
    };
  }, []);

  return (
    <>
      {!toggleDashboard && (
        <div className="border">
          <div className="bg-secondary space-y-3 p-5">
            <div className="flex justify-between">
              <Typography.Title level={3}>Loan Dashboard</Typography.Title>
              {/* only show the referrer action bar for mfd partners */}
              {scReferrers.length > 0 && (
                <ReferrerActionBar
                  selectedReferrer={selectedReferrer}
                  handleReferrerSelect={handleReferrerSelect}
                  scReferrersOption={scReferrersOption}
                  setSelectedReferrer={setSelectedReferrer}
                  startApplication={() => startApplication()}
                />
              )}
            </div>
            <div className="flex justify-between items-center">
              <LoanFilters
                resetFilters={resetFilters}
                filters={filters}
                setFilters={setFilters}
                fetchLoans={abortDownloadAndFetchLoans}
                partners={partners}
                onDownloadCSV={handleDownloadCSV}
                isDownloadDisabled={loans.length === 0}
              />
            </div>
          </div>
          <div className="flex justify-between items-center text-center p-5">
            <LoanUserTable
              filters={filters}
              setFilters={setFilters}
              setToggleDashboard={setToggleDashboard}
              showActionColumn={scReferrers.length > 0}
              isMfdPartner={isCurrentPartnerMfd()}
            />
          </div>
        </div>
      )}

      <div id={DASHBOARD_DIV_ID} className="border" />
    </>
  );
};

export default Dashboard;
